import React, { useMemo, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconCalendar, IconClock, IconInfoCircle } from '@tabler/icons-react';
import {
  LifecycleCohortRow,
  LifecycleData,
  LifecycleDrilldownAd,
} from './types';

export type LifecycleVelocityArgs = {
  goalKey: string;
  goalLabel: string;
  cohortKey: string;
  cohortKind: 'daily' | 'range' | 'weekday' | 'weekend' | 'summary';
  cohortStart?: string;
  cohortEnd?: string;
};

type GroupingKey = 'all' | 'grouped';

type DrilldownState = { date: string; goal: string } | null;

const fmtMoney = (v: number | string | null | undefined): string => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '$0.00';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtNumber = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '0';
  if (typeof v === 'number') return v.toLocaleString();
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
};

export const AggregatedLifecycleBody = ({
  lifecycleData,
  loading,
  isDarkTheme,
  onVelocityOpen,
}: {
  lifecycleData: LifecycleData | null;
  loading: boolean;
  isDarkTheme: boolean;
  onVelocityOpen?: (args: LifecycleVelocityArgs) => void;
}) => {
  const theme = useMantineTheme();
  const [grouping, setGrouping] = useState<GroupingKey>('all');
  const [drilldown, setDrilldown] = useState<DrilldownState>(null);
  const [drilldownGoalName, setDrilldownGoalName] = useState<string>('');
  const [drilldownAds, setDrilldownAds] = useState<LifecycleDrilldownAd[]>([]);

  const eligible = !!(lifecycleData && lifecycleData.eligible);
  const hasData = !!lifecycleData;
  const campaignGoals = lifecycleData?.campaign_goals ?? {};
  const goalEntries = useMemo(() => Object.entries(campaignGoals), [campaignGoals]);
  const dailyCohorts = lifecycleData?.daily_report_data ?? {};
  const summaries = lifecycleData?.summaries ?? {};
  const weekdayWeekendSummary = lifecycleData?.weekday_weekend_summaries ?? {};
  const dailyCohortDrilldown = lifecycleData?.daily_cohort_drilldown ?? {};
  const summariesDrilldown = lifecycleData?.cohort_drilldown ?? {};

  const displayCohorts = grouping === 'grouped' ? {} : dailyCohorts;
  const displaySummaries = grouping === 'grouped' ? weekdayWeekendSummary : summaries;

  const isDateStr = (v: unknown): v is string =>
    typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
  const resolveSummaryCohort = (
    row: LifecycleCohortRow | undefined,
  ): Pick<LifecycleVelocityArgs, 'cohortKind' | 'cohortStart' | 'cohortEnd'> => {
    const type = row?.cohort_type;
    const start = row?.cohort_start;
    const end   = row?.cohort_end;
    if (!isDateStr(start) || !isDateStr(end)) { return { cohortKind: 'summary' }; }
    if (type === 'weekday' || type === 'weekend') {
      return { cohortKind: type, cohortStart: start, cohortEnd: end };
    }
    return { cohortKind: 'range', cohortStart: start, cohortEnd: end };
  };

  const totalColumns = useMemo(() => {
    const goalsCount = goalEntries.length;
    return 3 + goalsCount * 4 + Math.max(0, goalsCount - 1) + 2;
  }, [goalEntries]);

  const handleDrilldown = (date: string, goalKey: string, goalName: string, kind: 'daily' | 'summary') => {
    setDrilldownGoalName(goalName);
    setDrilldown((prev) => (prev?.date === date && prev?.goal === goalKey ? null : { date, goal: goalKey }));
    const source = kind === 'summary' ? summariesDrilldown : dailyCohortDrilldown;
    const dd = source?.[date]?.[goalKey] ?? [];
    setDrilldownAds(Array.isArray(dd) ? dd : [dd]);
  };

  // Dark mode: one surface colour, faint row lines, thin group separators instead of tinted columns.
  const surfaceBg = isDarkTheme ? theme.colors.dark[6] : theme.colors.gray[0];
  const cellBorderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.06)' : theme.colors.gray[3];
  const edgeColor = isDarkTheme ? theme.colors.dark[4] : theme.colors.gray[4];
  const headerBg = isDarkTheme ? surfaceBg : theme.colors.gray[1];
  const summaryBg = isDarkTheme ? surfaceBg : theme.colors.gray[1];
  const revenueBg = isDarkTheme ? 'transparent' : '#FFF1F2';
  const goalBg = (color: string) => (isDarkTheme ? 'transparent' : color);
  const groupSeparator = isDarkTheme ? `1px solid ${edgeColor}` : undefined;
  const subHeaderColor = isDarkTheme ? theme.colors.dark[2] : '#000';
  const mutedColor = isDarkTheme ? theme.colors.dark[3] : undefined;
  const summaryLabelColor = isDarkTheme ? theme.colors.blue[4] : '#1c7ed6';
  const velocityIconColor = isDarkTheme ? 'gray' : 'indigo';

  // Zeros and dashes read muted in dark mode so real numbers stand out.
  const renderValue = (text: string) =>
    mutedColor && /^(-|—|\$?0(\.0+)?(%|x)?)$/.test(text) ? <span style={{ color: mutedColor }}>{text}</span> : text;
  // No spend means no cost per goal, so it reads "—" instead of $0.00; revenue per entry is money.
  const cpGoalText = (v: unknown) => (typeof v === 'number' && v > 0 ? `$${v.toFixed(2)}` : '—');
  const revenuePerEntryText = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? `$${v.toFixed(2)}` : '—');
  // Velocity icons and cost links stay quiet in dark mode: muted icon, no button box around the value.
  const velocityIconSx = isDarkTheme ? { color: mutedColor, opacity: 0.7, '&:hover': { opacity: 1 } } : undefined;
  const linkSx = { border: 0, background: 'transparent', padding: 0, '&:focus:not(:focus-visible)': { outline: 'none', boxShadow: 'none' } };

  // Entry date column stays pinned while the metric columns scroll under it.
  const stickyCell = (bg: string): React.CSSProperties => ({
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: bg,
    boxShadow: `inset -1px 0 0 ${edgeColor}`,
    whiteSpace: 'nowrap',
  });

  if (!loading && !hasData) {
    return (
      <Paper
        withBorder
        radius="md"
        p="md"
        mt="sm"
        sx={(t) => ({
          backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
        })}
      >
        <Text size="sm" color="dimmed" align="center" py="md">
          Select at least one Visiopt campaign to see the lifecycle cohorts.
        </Text>
      </Paper>
    );
  }
  if (!loading && hasData && !eligible) {
    return (
      <Paper
        withBorder
        radius="md"
        p="md"
        mt="sm"
        sx={(t) => ({
          backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
        })}
      >
        <Text size="sm" color="dimmed" align="center" py="md">
          No lifecycle data available for the current selection.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mt="sm"
      sx={(t) => ({
        backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
      })}
    >
      <Flex align="center" justify="space-between" mb="md" wrap="wrap" gap="md">
        <Flex align="center" gap="md" wrap="wrap">
          <Text size="sm" color="dimmed">Group by</Text>
          <Group spacing={6}>
            <Button
              size="xs"
              variant={grouping === 'all' ? 'filled' : 'default'}
              onClick={() => setGrouping('all')}
            >
              Daily Cohorts
            </Button>
            <Button
              size="xs"
              variant={grouping === 'grouped' ? 'filled' : 'default'}
              onClick={() => setGrouping('grouped')}
            >
              Weekday vs Weekend
            </Button>
          </Group>
          {lifecycleData?.selected_time && (
            <Group spacing={6}>
              <IconCalendar size={14} />
              <Text size="xs" color="dimmed">{lifecycleData.selected_time}</Text>
            </Group>
          )}
        </Flex>
        {lifecycleData?.attribution_model && (
          <Text size="xs" color="dimmed">
            Attribution: <Text component="span" weight={600}>{lifecycleData.attribution_model}</Text>
          </Text>
        )}
      </Flex>

      <ScrollArea type="auto" offsetScrollbars>
        <Table
          withBorder={!isDarkTheme}
          sx={{
            tableLayout: 'fixed',
            minWidth: 1300,
            borderColor: cellBorderColor,
            'thead tr th': {
              backgroundColor: headerBg,
              borderColor: cellBorderColor,
              whiteSpace: 'nowrap',
            },
            'tbody tr td': { borderColor: cellBorderColor, padding: '8px 12px', fontSize: 13 },
          }}
        >
          <colgroup>
            <col style={{ width: 230 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
            {goalEntries.map(([_, goal], gi) => (
              <React.Fragment key={`cg-${gi}`}>
                <col style={{ width: 100 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                {gi > 0 && <col style={{ width: 110 }} />}
              </React.Fragment>
            ))}
            <col style={{ width: 130 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...stickyCell(headerBg), zIndex: 2 }}>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>Entry Date</Text>
              </th>
              <th rowSpan={2}>
                <Tooltip
                  multiline
                  width={260}
                  withArrow
                  withinPortal
                  events={{ hover: true, focus: true, touch: true }}
                  label="Spend on the ads that brought converting customers. This can be lower than total spend elsewhere in the report."
                >
                  <Flex align="center" gap={4} sx={{ cursor: 'help', display: 'inline-flex' }} tabIndex={0}>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={600}>Total Spend</Text>
                    <IconInfoCircle size="0.8rem" />
                  </Flex>
                </Tooltip>
              </th>
              <th rowSpan={2}>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>Uniques</Text>
              </th>
              {goalEntries.map(([goalKey, goal], gi) => (
                <th
                  key={`gh-${goalKey}`}
                  colSpan={gi > 0 ? 5 : 4}
                  style={{ background: goalBg(goal.color), textAlign: 'center', color: isDarkTheme ? '#fff' : '#000', borderLeft: groupSeparator }}
                >
                  <Text size="xs" weight={700} transform="uppercase" align="center">
                    {goal.name}
                  </Text>
                </th>
              ))}
              <th colSpan={2} style={{ background: revenueBg, textAlign: 'center', color: isDarkTheme ? '#fff' : undefined, borderLeft: groupSeparator }}>
                <Text size="xs" weight={700} transform="uppercase" align="center">Revenue</Text>
              </th>
            </tr>
            <tr>
              {goalEntries.map(([goalKey, goal], gi) => {
                const headers = ['Conversions'];
                if (gi > 0) headers.push('% of Previous');
                headers.push('Conversion %', 'Cost Per Goal', 'Avg Days');
                return headers.map((h, hi) => (
                  <th
                    key={`gsh-${goalKey}-${h}`}
                    style={{
                      background: goalBg(goal.color),
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: 12,
                      whiteSpace: 'normal',
                      color: subHeaderColor,
                      borderLeft: hi === 0 ? groupSeparator : undefined,
                    }}
                  >
                    {h}
                  </th>
                ));
              })}
              <th style={{ background: revenueBg, textAlign: 'center', whiteSpace: 'normal', fontSize: 12, fontWeight: 600, color: isDarkTheme ? subHeaderColor : undefined, borderLeft: groupSeparator }}>
                Revenue per Entry (LTV)
              </th>
              <th style={{ background: revenueBg, textAlign: 'center', whiteSpace: 'normal', fontSize: 12, fontWeight: 600, color: isDarkTheme ? subHeaderColor : undefined }}>
                ROAS
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(displayCohorts).map(([date, data]) => (
              <React.Fragment key={`d-${date}`}>
                <tr>
                  <td style={{ ...stickyCell(surfaceBg), fontWeight: 600 }}>{date}</td>
                  <td>{renderValue(fmtMoney(typeof data.spend === 'number' ? data.spend : Number(data.spend)))}</td>
                  <td>{renderValue(fmtNumber(data.uniques))}</td>
                  {goalEntries.map(([goalKey, goal], gi) => {
                    const bg = goalBg(goal.color);
                    const conv = data[`${goalKey}_conversion`];
                    const convPct = data[`${goalKey}_conversion_perc`];
                    const prevPct = data[`${goalKey}_perc_prev`];
                    const cpGoal = data[`${goalKey}_cp_goal`];
                    const avg = data[`${goalKey}_avg_days`];
                    const convNum = typeof conv === 'number' ? conv : Number(conv) || 0;
                    const showVelocity = !!onVelocityOpen && convNum > 0;
                    return (
                      <React.Fragment key={`d-${date}-${goalKey}`}>
                        <td style={{ background: bg, textAlign: 'center', borderLeft: groupSeparator }}>
                          {showVelocity ? (
                            <Flex align="center" justify="center" gap={4}>
                              <span>{fmtNumber(conv)}</span>
                              <ActionIcon
                                variant="subtle"
                                size="xs"
                                color={velocityIconColor}
                                sx={velocityIconSx}
                                title="Velocity Report"
                                onClick={() =>
                                  onVelocityOpen!({
                                    goalKey,
                                    goalLabel: goal.name,
                                    cohortKey: date,
                                    cohortKind: 'daily',
                                  })
                                }
                              >
                                <IconClock size="0.85rem" />
                              </ActionIcon>
                            </Flex>
                          ) : (
                            renderValue(fmtNumber(conv))
                          )}
                        </td>
                        {gi > 0 && (
                          <td style={{ background: bg, textAlign: 'center' }}>
                            {renderValue(typeof prevPct === 'number' ? `${prevPct.toFixed(2)}%` : '—')}
                          </td>
                        )}
                        <td style={{ background: bg, textAlign: 'center' }}>
                          {renderValue(typeof convPct === 'number' ? `${convPct.toFixed(2)}%` : '—')}
                        </td>
                        <td style={{ background: bg, textAlign: 'center' }}>
                          {typeof conv === 'number' && conv > 0 ? (
                            <Anchor
                              component="button"
                              type="button"
                              fz={13}
                              sx={linkSx}
                              onClick={() => handleDrilldown(date, goalKey, goal.name, 'daily')}
                            >
                              {renderValue(cpGoalText(cpGoal))}
                            </Anchor>
                          ) : (
                            renderValue(cpGoalText(cpGoal))
                          )}
                        </td>
                        <td style={{ background: bg, textAlign: 'center' }}>
                          {renderValue(typeof avg === 'number' ? avg.toFixed(2) : '—')}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td style={{ background: revenueBg, textAlign: 'center', fontWeight: 600, borderLeft: groupSeparator }}>
                    {renderValue(revenuePerEntryText(data.revenue_per_entry))}
                  </td>
                  <td style={{ background: revenueBg, textAlign: 'center', fontWeight: 600 }}>
                    {renderValue(typeof data.roas === 'number' ? `${data.roas.toFixed(2)}x` : '—')}
                  </td>
                </tr>
                {drilldown?.date === date && (
                  <tr>
                    <td colSpan={totalColumns} style={{ padding: 0 }}>
                      <DrilldownPanel
                        ads={drilldownAds}
                        title={`${drilldownGoalName} – Ad-Level CP Goal Breakdown`}
                        isDarkTheme={isDarkTheme}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {Object.keys(displaySummaries).length > 0 && (
              <>
                <tr>
                  <td
                    style={{
                      ...stickyCell(summaryBg),
                      padding: '12px 16px',
                      borderTop: `2px solid ${edgeColor}`,
                      fontWeight: 700,
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      fontSize: 12,
                      color: mutedColor,
                    }}
                  >
                    Cohort Summaries
                  </td>
                  <td colSpan={totalColumns - 1} style={{ background: summaryBg, borderTop: `2px solid ${edgeColor}` }} />
                </tr>
                {Object.entries(displaySummaries).map(([key, data]) => (
                  <React.Fragment key={`s-${key}`}>
                    <tr style={{ background: summaryBg }}>
                      <td style={{ ...stickyCell(summaryBg), fontWeight: 700, color: summaryLabelColor }}>{data.summary_key ?? key}</td>
                      <td style={{ fontWeight: 700 }}>
                        {renderValue(fmtMoney(typeof data.spend === 'number' ? data.spend : Number(data.spend)))}
                      </td>
                      <td style={{ fontWeight: 700 }}>{renderValue(fmtNumber(data.uniques))}</td>
                      {goalEntries.map(([goalKey, goal], gi) => {
                        const bg = goalBg(goal.color);
                        const conv = data[`${goalKey}_conversion`];
                        const convPct = data[`${goalKey}_conversion_perc`];
                        const prevPct = data[`${goalKey}_perc_prev`];
                        const cpGoal = data[`${goalKey}_cp_goal`];
                        const avg = data[`${goalKey}_avg_days`];
                        const sConvNum = typeof conv === 'number' ? conv : Number(conv) || 0;
                        const showSumVelocity = !!onVelocityOpen && sConvNum > 0;
                        return (
                          <React.Fragment key={`s-${key}-${goalKey}`}>
                            <td style={{ background: bg, textAlign: 'center', fontWeight: 700, borderLeft: groupSeparator }}>
                              {showSumVelocity ? (
                                <Flex align="center" justify="center" gap={4}>
                                  <span>{fmtNumber(conv)}</span>
                                  <ActionIcon
                                    variant="subtle"
                                    size="xs"
                                    color={velocityIconColor}
                                    sx={velocityIconSx}
                                    title="Velocity Report"
                                    onClick={() =>
                                      onVelocityOpen!({
                                        goalKey,
                                        goalLabel: goal.name,
                                        cohortKey: key,
                                        ...resolveSummaryCohort(data),
                                      })
                                    }
                                  >
                                    <IconClock size="0.85rem" />
                                  </ActionIcon>
                                </Flex>
                              ) : (
                                renderValue(fmtNumber(conv))
                              )}
                            </td>
                            {gi > 0 && (
                              <td style={{ background: bg, textAlign: 'center', fontWeight: 700 }}>
                                {renderValue(typeof prevPct === 'number' ? `${prevPct.toFixed(2)}%` : '—')}
                              </td>
                            )}
                            <td style={{ background: bg, textAlign: 'center', fontWeight: 700 }}>
                              {renderValue(typeof convPct === 'number' ? `${convPct.toFixed(2)}%` : '—')}
                            </td>
                            <td style={{ background: bg, textAlign: 'center' }}>
                              <Anchor
                                component="button"
                                type="button"
                                fz={13}
                                fw={700}
                                sx={linkSx}
                                onClick={() => handleDrilldown(key, goalKey, goal.name, 'summary')}
                              >
                                {renderValue(cpGoalText(cpGoal))}
                              </Anchor>
                            </td>
                            <td style={{ background: bg, textAlign: 'center', fontWeight: 700 }}>
                              {renderValue(typeof avg === 'number' ? avg.toFixed(2) : '—')}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td style={{ background: revenueBg, textAlign: 'center', fontWeight: 700, borderLeft: groupSeparator }}>
                        {renderValue(revenuePerEntryText(data.revenue_per_entry))}
                      </td>
                      <td style={{ background: revenueBg, textAlign: 'center', fontWeight: 700 }}>
                        {renderValue(typeof data.roas === 'number' ? `${data.roas.toFixed(2)}x` : '—')}
                      </td>
                    </tr>
                    {drilldown?.date === key && (
                      <tr>
                        <td colSpan={totalColumns} style={{ padding: 0 }}>
                          <DrilldownPanel
                            ads={drilldownAds}
                            title={`${drilldownGoalName} – Ad-Level CP Goal Breakdown`}
                            isDarkTheme={isDarkTheme}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </ScrollArea>

      <Flex gap="lg" mt="md" wrap="wrap" align="center">
        {goalEntries.map(([goalKey, goal]) => (
          <Text key={`legend-${goalKey}`} size="xs" weight={600} sx={{ color: isDarkTheme ? '#e2e8f0' : '#1f2937' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                marginRight: 6,
                background: goal.color,
              }}
            />
            {goal.name}
          </Text>
        ))}
        <Text size="xs" color="dimmed">
          Cost/Goal = spend attributed to conversions at that goal
        </Text>
      </Flex>
    </Paper>
  );
};

const DrilldownPanel = ({
  ads,
  title,
  isDarkTheme,
}: {
  ads: LifecycleDrilldownAd[];
  title: string;
  isDarkTheme: boolean;
}) => {
  const bg = isDarkTheme ? '#23272e' : '#edf5ff';
  const border = isDarkTheme ? '#3a3d42' : '#dbe4ff';
  return (
    <Box p="md" sx={{ background: bg, borderTop: `1px solid ${border}` }}>
      <Text size="sm" weight={600} mb={8}>
        {title}
      </Text>
      <Table
        withBorder
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          fontSize: 12,
          '& th, & td': { padding: '8px 10px' },
          '& th': { fontWeight: 500, color: isDarkTheme ? '#cbd5e1' : '#495057', borderBottom: `1px solid ${border}` },
          '& td': { borderBottom: `1px solid ${border}` },
        }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Ad Name</th>
            <th style={{ textAlign: 'right' }}>Total Spend</th>
            <th style={{ textAlign: 'right' }}>Entries</th>
            <th style={{ textAlign: 'right' }}>CP Goal</th>
          </tr>
        </thead>
        <tbody>
          {ads.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center', color: '#9ca3af' }}>
                No contributing ads for this cohort.
              </td>
            </tr>
          ) : (
            ads.map((ad, idx) => (
              <tr key={`${ad.ad_id}-${idx}`}>
                <td style={{ textAlign: 'left' }}>{ad.ad_name}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(ad.spend)}</td>
                <td style={{ textAlign: 'right' }}>{fmtNumber(ad.entries)}</td>
                <td style={{ textAlign: 'right' }}>{typeof ad.cp_goal === 'number' && ad.cp_goal > 0 ? fmtMoney(ad.cp_goal) : '—'}</td>
              </tr>
            ))
          )}
        </tbody>
        {ads.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 600 }}>
              <td style={{ textAlign: 'left' }}>Total</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(ads.reduce((t, ad) => t + (Number(ad.spend) || 0), 0))}</td>
              <td style={{ textAlign: 'right' }}>{fmtNumber(ads.reduce((t, ad) => t + (Number(ad.entries) || 0), 0))}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </Table>
    </Box>
  );
};
